/**
 * Help Scout Beacon integration.
 *
 * The Beacon loader is injected in index.html, which defines a global
 * `window.Beacon` stub that queues calls until the real SDK script
 * (https://beacon-v2.helpscout.net) finishes loading.
 */

type BeaconEvent = 'open' | 'close' | 'ready';
type BeaconFn = (
  method: string,
  options?: unknown,
  data?: unknown
) => void;

declare global {
  interface Window {
    Beacon?: BeaconFn;
  }
}

export const HELP_SCOUT_BEACON_ID = '5955a161-e02e-4fe1-957c-90c0c32c957f';

const call = (method: string, options?: unknown, data?: unknown): void => {
  if (typeof window === 'undefined' || typeof window.Beacon !== 'function') return;
  window.Beacon(method, options, data);
};

let initialized = false;

export const initHelpScout = (beaconId: string = HELP_SCOUT_BEACON_ID): void => {
  if (initialized) return;
  call('init', beaconId);
  mountHelpScoutCloseOverlay();
  initialized = true;
};

/**
 * Help Scout's v2 Instant-Answers panel does not render its own close button
 * when used with a custom launcher. We overlay our own "×" button that sits
 * visually inside the top-right of the panel and calls Beacon('close').
 */
const mountHelpScoutCloseOverlay = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (document.getElementById('motr-hs-close-overlay')) return;

  const btn = document.createElement('button');
  btn.id = 'motr-hs-close-overlay';
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Close help panel');
  btn.title = 'Close';
  btn.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
  btn.style.cssText = [
    'position:fixed',
    'width:28px',
    'height:28px',
    'border-radius:50%',
    'border:none',
    'background:transparent',
    'color:#fff',
    'cursor:pointer',
    'display:none',
    'align-items:center',
    'justify-content:center',
    'padding:0',
    // Help Scout's beacon uses a very high z-index; go one higher.
    'z-index:2147483647',
    'transition:background-color 0.15s',
    'opacity:0.85',
  ].join(';');
  btn.addEventListener('mouseenter', () => {
    btn.style.background = 'rgba(255,255,255,0.2)';
    btn.style.opacity = '1';
  });
  btn.addEventListener('mouseleave', () => {
    btn.style.background = 'transparent';
    btn.style.opacity = '0.85';
  });
  btn.addEventListener('click', () => call('close'));
  document.body.appendChild(btn);

  /**
   * Find the Help Scout panel iframe. Its class names have changed across SDK
   * versions, so we pick the largest visible iframe pointing at helpscout.net.
   */
  const findPanel = (): HTMLElement | null => {
    const candidates = Array.from(document.querySelectorAll('iframe'));
    let best: HTMLIFrameElement | null = null;
    let bestArea = 0;
    for (const frame of candidates) {
      const src = frame.src || '';
      const title = (frame.title || '').toLowerCase();
      const looksLikeBeacon =
        src.includes('helpscout') ||
        title.includes('beacon') ||
        frame.id.toLowerCase().includes('beacon') ||
        frame.className.toLowerCase().includes('beacon');
      if (!looksLikeBeacon) continue;
      const rect = frame.getBoundingClientRect();
      const area = rect.width * rect.height;
      // Skip the FAB (tiny) — panel is substantially larger.
      if (area < 40000) continue;
      if (area > bestArea) {
        bestArea = area;
        best = frame;
      }
    }
    return best;
  };

  const position = () => {
    const panel = findPanel();
    if (!panel) {
      // Fallback: anchor to bottom-right of viewport matching beacon defaults.
      btn.style.top = `${Math.max(16, window.innerHeight - 640)}px`;
      btn.style.left = `${window.innerWidth - 60}px`;
      return;
    }
    const rect = panel.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    btn.style.top = `${rect.top + 12}px`;
    btn.style.left = `${rect.right - 48}px`;
  };

  let rafId: number | null = null;
  const reposition = () => {
    if (rafId != null) return;
    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      position();
    });
  };

  let pollId: number | null = null;
  const stopPolling = () => {
    if (pollId != null) {
      window.clearInterval(pollId);
      pollId = null;
    }
  };

  const handleOpen = () => {
    btn.style.display = 'flex';
    position();
    // Panel iframe is inserted asynchronously — keep repositioning while it settles.
    stopPolling();
    let tries = 0;
    pollId = window.setInterval(() => {
      tries += 1;
      position();
      if (tries > 40) stopPolling();
    }, 50);
  };

  const handleClose = () => {
    btn.style.display = 'none';
    stopPolling();
  };

  call('on', 'open', handleOpen);
  call('on', 'close', handleClose);

  window.addEventListener('resize', reposition);
  window.addEventListener('scroll', reposition, true);
};

export const identifyHelpScout = (user: { name?: string; email?: string }): void => {
  if (!user.email && !user.name) return;
  call('identify', { name: user.name, email: user.email });
};

export const openHelpScout = (): void => call('open');
export const closeHelpScout = (): void => call('close');
export const toggleHelpScout = (): void => call('toggle');

export const onHelpScoutEvent = (event: BeaconEvent, handler: () => void): void => {
  call('on', event, handler);
};

export const offHelpScoutEvent = (event: BeaconEvent, handler: () => void): void => {
  call('off', event, handler);
};

/** Clears the identified user — call this on logout. */
export const logoutHelpScout = (): void => call('logout');
